import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Card, Icon } from '@design-manager-inc/ui';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useGetMoodBoard } from 'api/hooks/useGetMoodBoard';
import { useLayout } from 'app/features/Layout/hooks/useLayout';
import { usePageViewed } from 'app/features/Analytics/hooks/usePageViewed';
import { PAGES } from 'app/features/Analytics/utils/constants';
import { routes } from 'app/features/Routing/Routes';
import { DefaultFallback } from 'app/features/SuspenseWrapper/SuspenseWrapper';
import { useFillAvailableHeight } from 'app/hooks/useFillAvailableHeight';
import { resolveBackgroundUrl } from './utils/background';
import { resolveSceneImages } from './utils/scene';

// Excalidraw is a heavy dependency and only this route needs it, so it is split
// into its own chunk rather than shipped in the main bundle. The repo bans
// direct default exports, hence the named-to-default hop.
const MoodBoardCanvas = lazy(async () => ({
	default: (await import('./partials/MoodBoardCanvas')).MoodBoardCanvas,
}));

// Below this the board stops shrinking and the page is allowed to scroll, so it
// stays usable on a short window.
const MIN_BOARD_HEIGHT = 420;

// `LayoutContent` insets its children, which would leave the board floating in a
// grey frame. Cancelling that padding lets it fill the container edge to edge.
// The values mirror what actually applies: `md:p-4` wins over the base `pt-6`
// from md up (`lg:pt-25` alongside it is not a real Tailwind spacing step and
// emits nothing), while `px-4` widens to `lg:px-16` and `2xl:px-20`.
const PAGE_BLEED = '-mt-6 md:-mt-4 -mx-4 -mb-2 md:-mb-4 lg:-mx-16 2xl:-mx-20';

// A single mood board (/moodboards/:id): the scene is view-only, with comment
// pins the client can add, edit and remove.
export const MoodBoard = () => {
	usePageViewed(PAGES.MOODBOARD);
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') || '';
	const { data: moodBoard, isLoading, isError } = useGetMoodBoard(token, id);
	const { ref, height, measure } = useFillAvailableHeight(MIN_BOARD_HEIGHT);

	// The board fills the whole content box, so the way back lives in the page
	// header rather than above the canvas.
	const backLink = useMemo(
		() => (
			<Link
				key="back"
				to={`/${routes.moodboards.path}?token=${token}`}
				className="flex items-center gap-1 text-sm font-semibold text-primary-badge"
				data-testid="mood-board-back"
			>
				<Icon name="arrow-left" className="fill-primary-badge" />
				Back
			</Link>
		),
		[token],
	);

	// The board's own name, once it has loaded. Falls back to the generic label
	// while the request is in flight and for a board saved without a title, so
	// the header is never blank.
	const title = moodBoard?.title || 'Mood Board';

	// The scene Excalidraw seeds from: the stored json with each image's URL
	// resolved from the board's `images`, so the canvas draws what the BE
	// currently serves rather than whatever URL was persisted in the scene.
	const scene = useMemo(
		() =>
			moodBoard ? resolveSceneImages(moodBoard.json, moodBoard.images) : null,
		[moodBoard],
	);

	// The board's background image, drawn behind the canvas; null when it has
	// none.
	const backgroundUrl = useMemo(
		() =>
			moodBoard
				? resolveBackgroundUrl(moodBoard.json, moodBoard.images)
				: null,
		[moodBoard],
	);

	useLayout(
		{
			header: {
				title,
				icon: routes.moodboards.icon,
				actions: [backLink],
			},
		},
		useMemo(() => [backLink, title], [backLink, title]),
	);

	// The header is filled in from an effect, so re-measure once it has settled
	// — a longer title can wrap and change its height.
	useEffect(() => {
		measure();
	}, [measure, backLink, title, isLoading]);

	if (isError) {
		return (
			<Card
				border="2xl"
				className="w-full p-5 bg-background flex flex-col items-center gap-1 text-center"
				data-testid="mood-board-error"
			>
				<Icon name="x-circle" size="lg" className="mb-4 fill-red-800" />
				<h3 className="text-lg font-semibold text-text">
					We couldn&apos;t load this mood board
				</h3>
				<p className="text-sm text-gray-500">
					Please try again in a few moments.
				</p>
			</Card>
		);
	}

	return (
		<div
			ref={ref}
			style={{ height }}
			className={PAGE_BLEED}
			data-testid="mood-board-page"
		>
			{/* The board is held back until the record resolves so Excalidraw seeds
			    from the real scene on its first (and only) mount. Plain `Suspense`
			    rather than `SuspenseWrapper`: the latter wraps children in an
			    unstyled div, which would break the height chain to the canvas. */}
			{isLoading ? (
				<DefaultFallback />
			) : (
				<Suspense fallback={<DefaultFallback />}>
					<MoodBoardCanvas
						scene={scene}
						backgroundUrl={backgroundUrl}
						moodBoardId={id}
						token={token}
						initialComments={moodBoard?.comments ?? []}
						onReady={measure}
					/>
				</Suspense>
			)}
		</div>
	);
};

MoodBoard.displayName = 'MoodBoard';
